import { useState } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import { testimonials, brandStats } from '@/mocks/testimonials';

const faqItems = [
  {
    q: '什么是 ASMR？',
    a: 'ASMR（自主感觉经络反应）是指通过特定的听觉或视觉刺激，在大脑和身体中产生一种愉悦的酥麻感，通常从头皮开始向下延伸。这种感受能帮助人们深度放松、缓解压力、改善睡眠。',
  },
  {
    q: 'asmrone 上的内容是免费的吗？',
    a: '是的，asmrone 上的所有音频内容均可免费收听。我们致力于让更多人享受到高品质 ASMR 带来的身心疗愈体验，无需付费即可畅享全部内容。',
  },
  {
    q: '如何选择适合自己的 ASMR 音频？',
    a: '你可以根据自己的需求来选择：助眠推荐雨声、耳语类内容；专注工作推荐白噪音、键盘打字类；放松解压推荐角色扮演、SPA 类。每个人的触发点不同，建议多尝试不同分类找到最适合自己的。',
  },
  {
    q: 'asmrone 的音频质量如何？',
    a: '我们采用专业双耳录音设备和高灵敏度麦克风进行录制，支持高保真音频输出。建议佩戴耳机聆听以获得最佳的沉浸式体验。',
  },
  {
    q: '可以在移动设备上使用吗？',
    a: '当然可以！asmrone 网站完全适配移动端，你可以在手机、平板等任何设备上通过浏览器访问。支持后台播放，锁屏后也能继续收听。',
  },
  {
    q: '如何联系 asmrone 团队？',
    a: '你可以通过下方的联系表单发送消息，或发送邮件至 hello@asmrone.com。我们通常会在 24 小时内回复。也欢迎在社交媒体上关注我们获取最新动态。',
  },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于 asmrone',
  description: 'asmrone 是专为 ASMR 爱好者打造的沉浸式音频体验平台，致力于用声音治愈每一个需要放松的人。',
  mainEntity: {
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  },
};

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q2n4nnahb7382ov580', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        form.reset();
      } else {
        setFormError(parsed?.meta?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们"
        description="了解 asmrone 的品牌故事、用户口碑与联系方式。asmrone 是专为 ASMR 爱好者打造的沉浸式音频体验平台，提供海量高品质 ASMR 内容。"
        keywords="asmrone,asmrone官网,关于asmrone,ASMR平台,品牌故事"
        canonicalUrl="https://asmrone.com/about"
        schema={aboutSchema}
      />

      <main className="min-h-screen pt-16">
        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-16">
              <div className="w-full lg:w-1/2">
                <div className="aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20cozy%20recording%20studio%20with%20professional%20microphone%2C%20warm%20lighting%2C%20headphones%20on%20wooden%20desk%2C%20cream%20walls%2C%20indoor%20plants%2C%20calm%20creative%20workspace%2C%20soft%20natural%20light%20from%20window&width=1000&height=750&seq=about-brand-01&orientation=landscape"
                    alt="asmrone 品牌工作室"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
                  关于 asmrone
                </h1>
                <div className="space-y-4 text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>asmrone</strong> 诞生于一群 ASMR 爱好者的共同梦想——让更多人享受到声音带来的治愈力量。我们相信，在这个快节奏的时代，每个人都值得拥有一段属于自己的宁静时光。
                  </p>
                  <p>
                    从 2024 年创立至今，asmrone 已经汇聚了超过 <strong>50 万</strong> 用户，收录了 <strong>2,000+</strong> 个精心制作的 ASMR 音频内容。我们与众多优秀的 ASMR 创作者合作，确保每一个音频都能带来最纯粹的沉浸式体验。
                  </p>
                  <p>
                    我们的使命很简单：<strong>用声音治愈你的每一天</strong>。无论是帮助失眠者安然入睡、为焦虑者带来平静，还是为专注者提供最佳背景音，asmrone 都在这里陪伴你。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-background-100/50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {brandStats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/60">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-4">
              用户口碑
            </h2>
            <p className="text-base text-foreground-600 text-center mb-12 max-w-xl mx-auto">
              来自真实用户的反馈，这是我们前进的最大动力
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 md:p-8 rounded-lg bg-background-100/70 border border-background-200/60"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-16 md:py-20 bg-background-100/50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-4">
              常见问题
            </h2>
            <p className="text-base text-foreground-600 text-center mb-12">
              你可能想了解的一些问题
            </p>

            <div className="flex flex-col gap-3">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="rounded-lg bg-background-50 border border-background-200/60 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="text-sm font-semibold text-foreground-900 pr-4">
                      {item.q}
                    </span>
                    <i
                      className={`text-foreground-400 text-sm transition-transform flex-shrink-0 ${
                        openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-base text-foreground-600 text-center mb-10">
              有任何问题或建议？欢迎随时联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 rounded-lg bg-background-100/70 border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">电子邮箱</div>
                <div className="text-sm font-medium text-foreground-800">hello@asmrone.com</div>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100/70 border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">微信公众号</div>
                <div className="text-sm font-medium text-foreground-800">asmrone 官方</div>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100/70 border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">工作时间</div>
                <div className="text-sm font-medium text-foreground-800">周一至周五 9:00-18:00</div>
              </div>
            </div>

            {submitted ? (
              <div className="text-center p-8 rounded-lg bg-accent-50 border border-accent-200">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                  消息已发送！
                </h3>
                <p className="text-sm text-foreground-600">
                  感谢你的来信，我们会在 24 小时内回复。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
                data-readdy-form="true"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="消息主题"
                  />
                </div>

                <div className="honeypot-wrap">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入你的留言内容（最多500字）"
                  ></textarea>
                </div>

                {formError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-600">{formError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? '发送中...' : '发送消息'}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
    </>
  );
}